import { useState, useEffect, useMemo } from 'react';
import useAuth from '../../hooks/useAuth';
import api from '../../services/api';
import {
  useDateTimeFilter,
  toBackendFormat,
  toInputFormat,
  getDefaultDateFrom,
  getDefaultDateTo,
} from '../../hooks/useDateTimeFilter';
import OperatorAnalyticsTable from '../../components/AnalyticsRA/OperatorAnalyticsTable';
import CriteriaAnalyticsTable from '../../components/AnalyticsRA/CriteriaAnalyticsTable';
import TopErrorsAnalyticsTable from '../../components/AnalyticsRA/TopErrorsAnalyticsTable';
import ChartComponent from '../../components/AnalyticsRA/ChartComponent';
import FilterModal from '../../components/AnalyticsRA/FilterModal';
import SendProjectReportModal from '../../components/AnalyticsRA/SendProjectReportModal';

import LoadingSpinner from '../../components/common/LoadingSpinner';

const ProjectAnalytics = () => {
  const { projects } = useAuth();
  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    if (projects && projects.length === 1) {
      return projects[0].id;
    }
    const saved = localStorage.getItem('analytics_ra_project_id');
    return saved ? parseInt(saved, 10) : null;
  });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [months, setMonths] = useState([]);
  const [selectedMonthYear, setSelectedMonthYear] = useState('');
  const [isDateManuallyChanged, setIsDateManuallyChanged] = useState(false);
  const [isSyncingFilters, setIsSyncingFilters] = useState(false); // Флаг для предотвращения повторного запроса при синхронизации
  const [isFetching, setIsFetching] = useState(false); // Флаг для блокировки повторных запросов
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showProjectSendReportModal, setShowProjectSendReportModal] = useState(false);

  // Удалено состояние chartSettings, так как настройки теперь управляются внутри LineChart

  // Сохранение проекта в localStorage
  useEffect(() => {
    if (selectedProjectId) {
      localStorage.setItem('analytics_ra_project_id', selectedProjectId);
    }
  }, [selectedProjectId]);

  const dateTimeFilter = useDateTimeFilter('', '');
  const {
    filters: dateFilters,
    tempValues: tempDateValues,
    handleChange: handleDateChange,
    handleBlur: handleDateBlur,
  } = dateTimeFilter;

  // Загрузка месяцев при выборе проекта
  useEffect(() => {
    const fetchMonths = async () => {
      if (!selectedProjectId) return;
      try {
        const monthsResponse = await api.get(`/quality/assessment-selection/assessment-months`, {
          params: { project_id: selectedProjectId }
        });
        if (monthsResponse.data.success) {
          setMonths(monthsResponse.data.months);
        }
      } catch (error) {
        console.error('Ошибка загрузки месяцев:', error);
      }
    };
    fetchMonths();
  }, [selectedProjectId]);

  // Отслеживаем ручное изменение дат
  useEffect(() => {
    if (tempDateValues.date_filter_from !== dateFilters.date_filter_from ||
        tempDateValues.date_filter_to !== dateFilters.date_filter_to) {
      setIsDateManuallyChanged(true);
    }
  }, [dateFilters.date_filter_from, dateFilters.date_filter_to, tempDateValues]);

  // Сброс месяца при ручном вводе дат
  useEffect(() => {
    if (isDateManuallyChanged && (dateFilters.date_filter_from || dateFilters.date_filter_to) && selectedMonthYear) {
      setSelectedMonthYear('');
    }
  }, [isDateManuallyChanged, dateFilters.date_filter_from, dateFilters.date_filter_to, selectedMonthYear]);

  // Загрузка данных
  useEffect(() => {
    // Прерываем выполнение, если идет синхронизация фильтров или уже выполняется запрос
    if (isSyncingFilters || isFetching) {
      console.log('Пропуск загрузки данных: идет синхронизация фильтров или уже выполняется запрос');
      return;
    }
    
    const fetchData = async () => {
      // Повторная проверка внутри, на всякий случай
      if (isFetching) return;
      
      if (!selectedProjectId) {
        console.log('Ожидание выбора проекта...');
        return;
      }
      
      setIsFetching(true);
      setLoading(true);
      setError(null);
      try {
        const monthYearParts = selectedMonthYear ? selectedMonthYear.split('-') : [];
        const month = monthYearParts[0] || '';
        const year = monthYearParts[1] || '';
        const dateFrom = dateFilters.date_filter_from ? toBackendFormat(dateFilters.date_filter_from) : '';
        const dateTo = dateFilters.date_filter_to ? toBackendFormat(dateFilters.date_filter_to) : '';

        const response = await api.get(`/quality/assessment-selection/with-assessment`, {
          params: {
            project_id: selectedProjectId,
            date_filter_from: dateFrom,
            date_filter_to: dateTo,
            month: month,
            year: year
          }
        });

        console.log('Данные с /quality/assessment-selection/with-assessment:', response.data);
        setData(response.data.data || []);

        // Устанавливаем флаг синхронизации ВСЕХ фильтров
        setIsSyncingFilters(true);
        
        // Синхронизация месяца с сервера
        if (response.data.filters?.month && response.data.filters?.year) {
          const monthYearValue = `${response.data.filters.month}-${response.data.filters.year}`;
          if (monthYearValue !== selectedMonthYear) {
            setSelectedMonthYear(monthYearValue);
          }
        }

        // Синхронизация дат с сервера
        if (response.data.filters) {
          const serverFrom = response.data.filters.date_filter_from || getDefaultDateFrom();
          const serverTo = response.data.filters.date_filter_to || getDefaultDateTo();
          const inputFrom = toInputFormat(serverFrom);
          const inputTo = toInputFormat(serverTo);

          if (inputFrom !== dateFilters.date_filter_from) {
            handleDateBlur({target: {name: 'date_filter_from', value: inputFrom}});
          }
          if (inputTo !== dateFilters.date_filter_to) {
            handleDateBlur({target: {name: 'date_filter_to', value: inputTo}});
          }
        }
        
        // Сбрасываем флаг после обновления ВСЕХ фильтров
        setTimeout(() => setIsSyncingFilters(false), 0);
      } catch (error)
      {
        console.error('Ошибка загрузки данных:', error);
        setError('Не удалось загрузить данные');
      } finally {
        setLoading(false);
        setIsFetching(false);
      }
    };

    fetchData();
  }, [selectedProjectId, dateFilters.date_filter_from, dateFilters.date_filter_to, selectedMonthYear]);

  // Получение уникальных agent_name из данных
  const agentNames = useMemo(() => {
    if (!data.length) return [];
    const nameSet = new Set();
    data.forEach(item => {
      if (item.assessment.agent_name) {
        nameSet.add(item.assessment.agent_name);
      }
    });
    return Array.from(nameSet);
  }, [data]);

  // Обработчик изменения чекбокса agent_name
  const handleAgentChange = (agentName) => {
    setSelectedAgents(prev => 
      prev.includes(agentName)
        ? prev.filter(name => name !== agentName) // Удаляем
        : [...prev, agentName] // Добавляем
    );
  };

  // Отфильтрованные данные
  const filteredData = useMemo(() => {
    if (!selectedAgents.length) return data;
    return data.filter(item => 
      item.assessment.agent_name && 
      selectedAgents.includes(item.assessment.agent_name)
    );
  }, [data, selectedAgents]);

  // Фильтрация данных по типу навыков для OperatorAnalyticsTable
  const softData = useMemo(() => {
    return filteredData.filter(item => item.assessment.total_count_soft > 0);
  }, [filteredData]);

  const hardData = useMemo(() => {
    return filteredData.filter(item => item.assessment.total_count_hard > 0);
  }, [filteredData]);

  const handleProjectChange = (e) => {
    const projectId = e.target.value ? parseInt(e.target.value, 10) : null;
    setSelectedProjectId(projectId);
    setSelectedMonthYear('');
    handleDateBlur({ target: { name: 'date_filter_from', value: '' } });
    handleDateBlur({ target: { name: 'date_filter_to', value: '' } });
    setIsDateManuallyChanged(false);
  };

  // Обработчик отправки отчета по проекту (заглушка)
  const handleSendProjectReport = () => {
    // Подготовленные данные для метода отправки
    const reportData = {
      project_id: selectedProjectId,
      selectedMonthYear: selectedMonthYear,
      dateFrom: dateFilters.date_filter_from,
      dateTo: dateFilters.date_filter_to,
      filteredData: filteredData,
      totalAssessments: filteredData.length,
      avgScore: avg_total_score
    };
    
    console.log('Данные для отправки отчета по проекту:', reportData);
    
    // TODO: Реализовать отправку отчета по проекту через API
    // Отправка отчета через API
    try {
      // await api.post('/quality/assessment-selection/send-project-report', reportData);
      console.log('Отчет по проекту отправлен успешно!');
    } catch (error) {
      console.error('Ошибка при отправке отчета по проекту:', error);
    }
  };

  const handleMonthChange = (e) => {
    const value = e.target.value;
    setSelectedMonthYear(value);
    if (value === '') {
      handleDateBlur({ target: { name: 'date_filter_from', value: '' } });
      handleDateBlur({ target: { name: 'date_filter_to', value: '' } });
    }
  };

  const getMonthName = (monthNumber) => {
    const months = [
      'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
      'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
    ];
    return months[monthNumber - 1] || '';
  };

  const monthYearOptions = months.map(m => ({
    value: `${m.month}-${m.year}`,
    label: `${getMonthName(m.month)} ${m.year}`
  }));

  const showCustomOption = isDateManuallyChanged && (dateFilters.date_filter_from || dateFilters.date_filter_to) && !selectedMonthYear;
  // Статистика по Hard и Soft skill
  const stats = useMemo(() => {
    let hardCount = 0;
    let softCount = 0;
    let hardScoreSum = 0;
    let softScoreSum = 0;

    filteredData.forEach(item => {
      const assessment = item.assessment;
      if (assessment.total_count_hard > 0) {
        hardCount += 1;
        hardScoreSum += assessment.total_score_hard || 0;
      }
      if (assessment.total_count_soft > 0) {
        softCount += 1;
        softScoreSum += assessment.total_score_soft || 0;
      }
    });

    const hardAvg = hardCount > 0 ? (hardScoreSum / hardCount * 100).toFixed(2) : '0.00';
    const softAvg = softCount > 0 ? (softScoreSum / softCount * 100).toFixed(2) : '0.00';

    return { hardCount, softCount, hardAvg, softAvg };
  }, [filteredData]);

  let avg_total_score = 0.0;
  if (filteredData.length > 0) {
    avg_total_score = (filteredData.reduce((sum, item) => sum + item.assessment.total_score, 0) / filteredData.length * 100).toFixed(2);
  }
  if (!selectedProjectId) {
    return (
      <div className="analytics-container">
        <h2>Аналитика по проекту</h2>
        {projects.length === 0 ? (
        <p>У вас нет доступных проектов.</p>
      ) : (
        <div className="form-group" style={{ marginBottom: '20px' }}>
          <label htmlFor="projectSelect">Выберите проект:</label>
          <select
            id="projectSelect"
            value={selectedProjectId || ''}
            onChange={handleProjectChange}
            className="project-select"
            style={{
              padding: '8px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              fontSize: '14px'
            }}
          >
            <option value="">-- Выберите проект --</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name_text}
              </option>
            ))}
          </select>
        </div>
      )}
      </div>
    )
  }
  return (
    <div className="analytics-container">
      <h2>Аналитика по проекту</h2>

      {/* Модальное окно фильтров */}
      <FilterModal
        show={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        dateFilters={dateFilters}
        tempValues={tempDateValues}
        handleDateChange={handleDateChange}
        handleDateBlur={handleDateBlur}
        selectedMonthYear={selectedMonthYear}
        monthYearOptions={monthYearOptions}
        handleMonthChange={handleMonthChange}
        agentNames={agentNames}
        selectedAgents={selectedAgents}
        handleAgentChange={handleAgentChange}
        showCustomOption={showCustomOption}
      />

      {/* Модальное окно отправки отчета по проекту */}
      <SendProjectReportModal
        show={showProjectSendReportModal}
        onClose={() => setShowProjectSendReportModal(false)}
        project_id={selectedProjectId}
        selectedMonthYear={selectedMonthYear}
        dateFrom={dateFilters.date_filter_from}
        dateTo={dateFilters.date_filter_to}
        filteredData={filteredData}
        totalAssessments={filteredData.length}
        avgScore={avg_total_score}
        selectedAgents={selectedAgents}
      />

      {projects.length === 0 ? (
        <p>У вас нет доступных проектов.</p>
      ) : (
        <div className="form-group" style={{ marginBottom: '20px' }}>
          <label htmlFor="projectSelect">Выберите проект:</label>
          <select
            id="projectSelect"
            value={selectedProjectId || ''}
            onChange={handleProjectChange}
            className="project-select"
            style={{
              padding: '8px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              fontSize: '14px'
            }}
          >
            <option value="">-- Выберите проект --</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name_text}
              </option>
            ))}
          </select>
        </div>
      )}
      {/* Кнопка открытия модального окна фильтров и кнопка отправки отчета */}
      <div className="mb-4" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowFilterModal(true)}
            style={{
              padding: '0.375rem 0.75rem',
              fontSize: '1rem',
              borderRadius: '0.25rem'
            }}
          >
            Фильтры
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '16px', color: '#555' }}>
          <span>С: {(dateFilters.date_filter_from || '—').replace(/-/g, '.')}</span>
          <span>До: {(dateFilters.date_filter_to || '—').replace(/-/g, '.')}</span>
        </div>
        <div>
          <button
            type="button"
            className="btn btn-info"
            onClick={() => setShowProjectSendReportModal(true)}
            style={{
              padding: '0.375rem 0.75rem',
              fontSize: '1rem',
              borderRadius: '0.25rem',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>📧</span>
            <span>Отправить отчет</span>
          </button>
        </div>
      </div>

      
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div className="analytics-ra-stats-container">


            <div className="analytics-ra-stats-card">
              <h4>Количество оценок Soft skill</h4>
              <div className="value count">
                {stats.softCount}
              </div>
            </div>

            <div className="analytics-ra-stats-card">
              <h4>Средний балл Soft skill</h4>
              <div className="value score"
                   style={{color: stats.softAvg >= 97 ? 'green' : stats.softAvg >= 95 ? 'orange' : 'red'}}>
                {stats.softAvg}%
              </div>
            </div>

            <div className="analytics-ra-stats-card">
              <h4>Количество оценок Hard skill</h4>
              <div className="value count">
                {stats.hardCount}
              </div>
            </div>

            <div className="analytics-ra-stats-card">
              <h4>Средний балл Hard skill</h4>
              <div className="value score"
                   style={{color: stats.hardCount <= 0 ? 'black': stats.hardAvg >= 97 ? 'green' : stats.hardAvg >= 95 ? 'orange' : 'red'}}>
                { stats.hardCount >0 ?stats.hardAvg +'%' : '-'}
              </div>
            </div>


          </div>

          <OperatorAnalyticsTable data={softData} loading={loading} name="Аналитика по операторам (Soft skill)" skillType="soft" />
          <OperatorAnalyticsTable data={hardData} loading={loading} name="Аналитика по операторам (Hard skill)" skillType="hard" />

          <TopErrorsAnalyticsTable data={filteredData} loading={loading}/>

          <CriteriaAnalyticsTable data={filteredData} loading={loading}/>

          <ChartComponent
              data={filteredData}
            loading={loading} 
            // Передаем только базовые настройки, все управление теперь внутри LineChart
            threshold={{ value: 97, label: 'Норма', color: '#ff0000' }}
            onExport={true}
          />
        </>
      )
      }
    </div>
  );
};

export default ProjectAnalytics;